import { get, post, patch, del } from '../httpMethods';
import { ApiResponse, PaginationMeta } from '../../types/api';

export interface PharmacyDrugCategory {
  id: string;
  name: string;
  code?: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
}

export interface PharmacyGenericName {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
}

export interface PharmacyCompany {
  id: string;
  name: string;
  code?: string;
  description?: string;
  contact?: string;
  isActive: boolean;
  createdAt: string;
}

export interface PharmacyStorageRack {
  id: string;
  name: string;
  location?: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
}

export type DrugForm =
  | 'TABLET'
  | 'CAPSULE'
  | 'SYRUP'
  | 'SUSPENSION'
  | 'INJECTION'
  | 'OINTMENT'
  | 'CREAM'
  | 'DROPS'
  | 'INHALER'
  | 'POWDER'
  | 'OTHER';

export type PharmacyItemType = 'MEDICINE' | 'ACCESSORY';

export interface PharmacyDrug {
  id: string;
  drugCode: string;
  name: string;
  form: DrugForm;
  itemType: PharmacyItemType;
  strength?: string;
  categoryId: string;
  category: PharmacyDrugCategory;
  genericNameId: string;
  genericName: PharmacyGenericName;
  companyId: string;
  company: PharmacyCompany;
  storageRackId?: string;
  storageRack?: PharmacyStorageRack;
  unitOfMeasure: string;
  price: number;
  reorderLevel: number;
  reorderQuantity: number;
  description?: string;
  isActive: boolean;
  createdAt: string;
  /** Live stock level derived from the pharmacy ledger (list endpoint only). */
  currentStock?: number;
  isLowStock?: boolean;
}

export type PharmacyStaffRole = 'PHARMACIST' | 'ASSISTANT' | 'CASHIER' | 'MANAGER';
export type PharmacyShift = 'MORNING' | 'AFTERNOON' | 'EVENING' | 'NIGHT' | 'FULL_DAY';

export interface PharmacyStaffAssignment {
  id: string;
  employeeId: string;
  employee: { id: string; employeeCode: string; name: string; firstName?: string; lastName?: string; designation?: { name: string } };
  role: PharmacyStaffRole;
  shift: PharmacyShift;
  assignedFrom?: string;
  assignedTo?: string;
  isActive: boolean;
  notes?: string;
  createdAt: string;
}

export interface CreatePharmacyDrugDto {
  drugCode: string;
  name: string;
  form?: DrugForm;
  itemType?: PharmacyItemType;
  strength?: string;
  categoryId: string;
  genericNameId: string;
  companyId: string;
  storageRackId?: string;
  unitOfMeasure: string;
  price?: number;
  reorderLevel?: number;
  reorderQuantity?: number;
  description?: string;
  isActive?: boolean;
}

export interface CreateLookupDto {
  name: string;
  code?: string;
  description?: string;
  contact?: string;
  location?: string;
  isActive?: boolean;
}

export interface CreateStaffAssignmentDto {
  employeeId: string;
  role?: PharmacyStaffRole;
  shift?: PharmacyShift;
  assignedFrom?: string;
  assignedTo?: string;
  isActive?: boolean;
  notes?: string;
}

export interface PharmacySyncStatus {
  syncId: string;
  state: 'pending' | 'searching' | 'processing' | 'completed' | 'failed' | 'stopped';
  discovered: number;
  processed: number;
  failed: number;
  searchTotal: number;
  searchCompleted: number;
  searchFailed: number;
  pending: number;
}

export const pharmacyDrugService = {
  // Drugs
  getDrugs: (page = 1, limit = 20, filters?: {
    search?: string;
    categoryId?: string;
    genericNameId?: string;
    companyId?: string;
    storageRackId?: string;
    lowOnly?: boolean;
  }): Promise<ApiResponse<PharmacyDrug[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    Object.entries(filters || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value));
      }
    });
    return get<PharmacyDrug[]>(`/pharmacy/drugs?${params.toString()}`) as Promise<ApiResponse<PharmacyDrug[]> & { meta: PaginationMeta }>;
  },

  getDrug: (id: string): Promise<ApiResponse<PharmacyDrug>> => {
    return get<PharmacyDrug>(`/pharmacy/drugs/${id}`);
  },

  createDrug: (data: CreatePharmacyDrugDto): Promise<ApiResponse<PharmacyDrug>> => {
    return post<PharmacyDrug>('/pharmacy/drugs', data);
  },

  updateDrug: (id: string, data: Partial<CreatePharmacyDrugDto>): Promise<ApiResponse<PharmacyDrug>> => {
    return patch<PharmacyDrug>(`/pharmacy/drugs/${id}`, data);
  },

  deleteDrug: (id: string): Promise<ApiResponse<void>> => {
    return del<void>(`/pharmacy/drugs/${id}`);
  },

  // Categories
  getCategories: (page = 1, limit = 20, search?: string): Promise<ApiResponse<PharmacyDrugCategory[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.append('search', search);
    return get<PharmacyDrugCategory[]>(`/pharmacy/drug-categories?${params.toString()}`) as Promise<ApiResponse<PharmacyDrugCategory[]> & { meta: PaginationMeta }>;
  },

  createCategory: (data: CreateLookupDto): Promise<ApiResponse<PharmacyDrugCategory>> => {
    return post<PharmacyDrugCategory>('/pharmacy/drug-categories', data);
  },

  updateCategory: (id: string, data: Partial<CreateLookupDto>): Promise<ApiResponse<PharmacyDrugCategory>> => {
    return patch<PharmacyDrugCategory>(`/pharmacy/drug-categories/${id}`, data);
  },

  deleteCategory: (id: string): Promise<ApiResponse<void>> => {
    return del<void>(`/pharmacy/drug-categories/${id}`);
  },

  // Generic Names
  getGenericNames: (page = 1, limit = 20, search?: string): Promise<ApiResponse<PharmacyGenericName[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.append('search', search);
    return get<PharmacyGenericName[]>(`/pharmacy/generic-names?${params.toString()}`) as Promise<ApiResponse<PharmacyGenericName[]> & { meta: PaginationMeta }>;
  },

  createGenericName: (data: CreateLookupDto): Promise<ApiResponse<PharmacyGenericName>> => {
    return post<PharmacyGenericName>('/pharmacy/generic-names', data);
  },

  updateGenericName: (id: string, data: Partial<CreateLookupDto>): Promise<ApiResponse<PharmacyGenericName>> => {
    return patch<PharmacyGenericName>(`/pharmacy/generic-names/${id}`, data);
  },

  deleteGenericName: (id: string): Promise<ApiResponse<void>> => {
    return del<void>(`/pharmacy/generic-names/${id}`);
  },

  // Companies
  getCompanies: (page = 1, limit = 20, search?: string): Promise<ApiResponse<PharmacyCompany[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.append('search', search);
    return get<PharmacyCompany[]>(`/pharmacy/companies?${params.toString()}`) as Promise<ApiResponse<PharmacyCompany[]> & { meta: PaginationMeta }>;
  },

  createCompany: (data: CreateLookupDto): Promise<ApiResponse<PharmacyCompany>> => {
    return post<PharmacyCompany>('/pharmacy/companies', data);
  },

  updateCompany: (id: string, data: Partial<CreateLookupDto>): Promise<ApiResponse<PharmacyCompany>> => {
    return patch<PharmacyCompany>(`/pharmacy/companies/${id}`, data);
  },

  deleteCompany: (id: string): Promise<ApiResponse<void>> => {
    return del<void>(`/pharmacy/companies/${id}`);
  },

  // Storage Racks
  getStorageRacks: (): Promise<ApiResponse<PharmacyStorageRack[]>> => {
    return get<PharmacyStorageRack[]>('/pharmacy/storage-racks');
  },

  createStorageRack: (data: CreateLookupDto): Promise<ApiResponse<PharmacyStorageRack>> => {
    return post<PharmacyStorageRack>('/pharmacy/storage-racks', data);
  },

  updateStorageRack: (id: string, data: Partial<CreateLookupDto>): Promise<ApiResponse<PharmacyStorageRack>> => {
    return patch<PharmacyStorageRack>(`/pharmacy/storage-racks/${id}`, data);
  },

  deleteStorageRack: (id: string): Promise<ApiResponse<void>> => {
    return del<void>(`/pharmacy/storage-racks/${id}`);
  },

  // Staff Assignments
  getStaffAssignments: (): Promise<ApiResponse<PharmacyStaffAssignment[]>> => {
    return get<PharmacyStaffAssignment[]>('/pharmacy/staff-assignments');
  },

  createStaffAssignment: (data: CreateStaffAssignmentDto): Promise<ApiResponse<PharmacyStaffAssignment>> => {
    return post<PharmacyStaffAssignment>('/pharmacy/staff-assignments', data);
  },

  updateStaffAssignment: (id: string, data: Partial<CreateStaffAssignmentDto>): Promise<ApiResponse<PharmacyStaffAssignment>> => {
    return patch<PharmacyStaffAssignment>(`/pharmacy/staff-assignments/${id}`, data);
  },

  deleteStaffAssignment: (id: string): Promise<ApiResponse<void>> => {
    return del<void>(`/pharmacy/staff-assignments/${id}`);
  },

  // Medicine API sync
  syncMedicines: (options?: { queries?: string[]; clearExisting?: boolean }): Promise<ApiResponse<{ syncId: string; queries: string[] }>> => {
    return post<{ syncId: string; queries: string[] }>('/pharmacy/sync', {
      queries: options?.queries,
      clearExisting: options?.clearExisting,
    });
  },

  getSyncStatus: (syncId: string): Promise<ApiResponse<PharmacySyncStatus>> => {
    return get<PharmacySyncStatus>(`/pharmacy/sync/${syncId}/status`);
  },

  stopSync: (syncId: string): Promise<ApiResponse<void>> => {
    return post<void>(`/pharmacy/sync/${syncId}/stop`);
  },
};
